import React, { ReactElement } from 'react';
import { NavLink, To } from 'hotel/navigation';
import { twMerge } from 'tailwind-merge';

type ButtonProps = Partial<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > & {
    text?: string;
    size?: 'sm' | 'base';
  }
>;
type ButtonLinkProps = {
  className?: string;
  text?: string;
  to: To;
  state?: Record<string, unknown>;
  children?: ReactElement;
  disabled?: boolean;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'disabled'>;

export const baseButtonClass = 'btn-base';
export const baseButtonSmClass = 'btn-base-sm';
export const primaryButtonClass =
  'bg-primaryBlue text-white ring-primaryBlue hover:bg-primaryBlueShade focus:bg-primaryBlueShade';
export const secondaryButtonClass =
  'bg-secondaryBlue text-white ring-secondaryBlueShade hover:bg-secondaryBlueShade focus:bg-secondaryBlueShade';
export const accentButtonClass =
  'bg-accentGreen text-white ring-accentGreenShade hover:bg-accentGreenShade focus:bg-accentGreenShade';
export const tealButtonClass =
  'bg-teal text-white ring-teal hover:bg-tealShade focus:bg-tealShade';
export const redButtonClass =
  'bg-white text-red-500 ring-gray-200 hover:bg-gray-400 focus:bg-red-500';

export function Button({
  className,
  children,
  text,
  size = 'base',
  ...props
}: ButtonProps) {
  return (
    <button
      className={twMerge(
        size === 'sm' ? baseButtonSmClass : baseButtonClass,
        className,
      )}
      {...props}
    >
      {children || text}
    </button>
  );
}

export function ButtonLink({
  className,
  text,
  children,
  state,
  disabled = false,
  ...props
}: ButtonLinkProps) {
  return (
    <NavLink
      className={twMerge(
        baseButtonClass,
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
      state={state}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </NavLink>
  );
}

export function PrimaryButton({ className, ...props }: ButtonProps) {
  return (
    <Button className={twMerge(primaryButtonClass, className)} {...props} />
  );
}
export function PrimaryButtonLink({ className, ...props }: ButtonLinkProps) {
  return (
    <ButtonLink className={twMerge(primaryButtonClass, className)} {...props} />
  );
}

export function SecondaryButton({ className, ...props }: ButtonProps) {
  return (
    <Button className={twMerge(secondaryButtonClass, className)} {...props} />
  );
}
export function SecondaryButtonLink({ className, ...props }: ButtonLinkProps) {
  return (
    <ButtonLink
      className={twMerge(secondaryButtonClass, className)}
      {...props}
    />
  );
}

export function AccentButton({ className, ...props }: ButtonProps) {
  return (
    <Button className={twMerge(accentButtonClass, className)} {...props} />
  );
}
export function AccentButtonLink({ className, ...props }: ButtonLinkProps) {
  return (
    <ButtonLink className={twMerge(accentButtonClass, className)} {...props} />
  );
}

export function TealButton({ className, ...props }: ButtonProps) {
  return <Button className={twMerge(tealButtonClass, className)} {...props} />;
}
export function TealButtonLink({ className, ...props }: ButtonLinkProps) {
  return (
    <ButtonLink className={twMerge(tealButtonClass, className)} {...props} />
  );
}

export function TealButtonSm({ className, ...props }: ButtonProps) {
  return (
    <Button
      className={twMerge(tealButtonClass, className)}
      size="sm"
      {...props}
    />
  );
}

export function RedButton({ className, ...props }: ButtonProps) {
  return <Button className={twMerge(redButtonClass, className)} {...props} />;
}
